import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useChain } from '@cosmos-kit/react';
import { StargateClient } from '@cosmjs/stargate';

// Query key factory
const balanceKeys = {
  all: ['balance'] as const,
  gravity: (address: string) => [...balanceKeys.all, 'gravity', address] as const,
};

interface Balance {
  amount: string;
  denom: string;
}

/**
 * Hook to fetch GRAV balance for a Gravity Bridge address
 * Uses cosmos-kit's chain configuration for RPC endpoint
 * @param address Gravity Bridge address (optional)
 * @returns Query result with balance in ugraviton
 */
export function useGravityBalance(address: string | undefined): UseQueryResult<string, Error> {
  const { chain } = useChain('gravitybridge');
  
  return useQuery<string, Error>({
    queryKey: balanceKeys.gravity(address ?? ''),
    queryFn: async () => {
      if (address === undefined || address === '') {
        return '0';
      }

      try {
        // Get RPC endpoint from chain config
        const rpcEndpoint = chain.apis?.rpc?.[0]?.address;
        if (rpcEndpoint === undefined) {
          throw new Error('No RPC endpoint available');
        }

        // Connect to Gravity Bridge RPC
        const client = await StargateClient.connect(rpcEndpoint);
        
        // Get balance for ugraviton (native token)
        const balance = await client.getBalance(address, 'ugraviton');
        
        client.disconnect();
        
        return balance.amount;
      } catch (error) {
        console.error('Error fetching Gravity balance:', error);
        throw error;
      }
    },
    enabled: address !== undefined && address !== '',
    staleTime: 10_000, // 10 seconds
    refetchInterval: 30_000, // Auto-refresh every 30 seconds
  });
}

/**
 * Hook to fetch all balances for a Gravity Bridge address
 * @param address Gravity Bridge address (optional)
 * @returns Query result with all token balances
 */
export function useAllGravityBalances(address: string | undefined): UseQueryResult<Balance[], Error> {
  const { chain } = useChain('gravitybridge');
  
  return useQuery<Balance[], Error>({
    queryKey: [...balanceKeys.gravity(address ?? ''), 'all'],
    queryFn: async () => {
      if (address === undefined || address === '') {
        return [];
      }

      try {
        const rpcEndpoint = chain.apis?.rpc?.[0]?.address;
        if (rpcEndpoint === undefined) {
          throw new Error('No RPC endpoint available');
        }

        const client = await StargateClient.connect(rpcEndpoint);
        const balances = await client.getAllBalances(address);
        client.disconnect();
        
        return balances.map(b => ({
          amount: b.amount,
          denom: b.denom,
        }));
      } catch (error) {
        console.error('Error fetching all Gravity balances:', error);
        throw error;
      }
    },
    enabled: address !== undefined && address !== '',
    staleTime: 10_000, // 10 seconds
    refetchInterval: 30_000, // Auto-refresh every 30 seconds
  });
}

/**
 * Hook that wraps useGravityBalance with the current connected wallet
 * Convenient shorthand for getting the current user's balance
 */
export function useConnectedGravityBalance(): UseQueryResult<string, Error> {
  const { address } = useChain('gravitybridge');
  return useGravityBalance(address);
}
